import type { ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from 'antd';

export interface KPICardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  color: string;
  suffix?: string;
  trend?: number;
  loading?: boolean;
  testId?: string;
}

export function KPICard({
  label,
  value,
  icon: Icon,
  color,
  suffix,
  trend,
  loading = false,
  testId,
}: KPICardProps): ReactElement {
  return (
    <Card
      size="small"
      bordered={false}
      style={{ 
        height: '100%', 
        borderRadius: 14,
        border: '1px solid #e7e4db',
        boxShadow: 'none',
        background: '#ffffff'
      }}
      styles={{ 
        body: { 
          padding: '16px 20px', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between',
          height: '100%',
          minHeight: '110px'
        } 
      }}
    >
        <div className="flex justify-between items-start w-full gap-2">
          <span className="font-semibold tracking-wider uppercase truncate" style={{ color: '#a09483', fontSize: 11 }}>
            {label}
          </span>
          {Icon && (
            <Icon size={14} strokeWidth={2} color={color || '#d97706'} className="shrink-0 leading-none mt-0.5" />
          )}
        </div>

        <div className="flex flex-col mt-3">
          <div className="flex items-baseline overflow-hidden">
            <span className="font-mono tracking-tight font-medium leading-none truncate" data-testid={testId} style={{ color: '#1f1a14', fontSize: 34 }}>
              {loading ? '–' : value}
            </span>
            {suffix && (
              <span className="font-medium whitespace-nowrap" style={{ color: '#a09483', fontSize: 12, marginLeft: 2 }}>
                {suffix}
              </span>
            )}
          </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1.5 mt-3">
            <span style={{ fontSize: 12, padding: '2px 6px', borderRadius: 100, backgroundColor: trend > 0 ? '#f0fdf4' : trend < 0 ? '#fef2f2' : '#f5f5f4', color: trend > 0 ? '#16a34a' : trend < 0 ? '#dc2626' : '#78716c', fontWeight: 600 }}>
              {trend > 0 ? '↑' : trend < 0 ? '↓' : ''} {Math.abs(trend)}%
            </span>
            <span style={{ fontSize: 11, color: '#a8a29e', fontWeight: 500 }}>vs last period</span>
          </div>
        )}
      </div>
    </Card>
  );
}
